import type { ColDef, GridApi, ICellRendererParams, IRowNode } from 'ag-grid-enterprise'
import consola from 'consola'
import { StcsFpnaTypeEnum } from '~/protos/gen/stcs_enums.entity'
import { useAcmStore } from '~/stores/acm.store'
import { OnboardingCustomHeader } from '#components'
import { useNuxtApp } from '#imports'

interface ActionButtonRendererParams extends ICellRendererParams {
  gridApi: GridApi
}

interface GridActionButtonInterface {
  eGui: HTMLElement
  params: ActionButtonRendererParams

  isEnabled(node: IRowNode, gridApi: GridApi): boolean

  onClick(event: MouseEvent, node: IRowNode, gridApi: GridApi): void

  getColDef(gridApi: GridApi): ColDef
}

export abstract class AcmGridActionButtonStrategy implements GridActionButtonInterface {
  params!: ActionButtonRendererParams
  eGui!: HTMLElement
  abstract headerName: string
  abstract id: string
  headerTooltip = ''
  minWidth = 150
  flex = 0
  acmStore = useAcmStore()
  $tolgee = useNuxtApp().$tolgee

  init(params: ActionButtonRendererParams) {
    this.params = params
    this.render()
  }

  render() {
    if (this.isEnabled(this.params.node, this.params.gridApi)) {
      this.eGui = this.getElement(this.params.node, this.params.gridApi)
      this.eGui.addEventListener('click', (event: MouseEvent) => {
        event.stopPropagation()
        this.onClick(event, this.params.node, this.params.gridApi)
      })
    }
  }

  getGui() {
    return this.eGui
  }

  refresh(params: ActionButtonRendererParams) {
    this.params = params
    this.render()
    return true
  }

  public isActionAllowed(node: IRowNode): boolean {
    switch (node?.data?.fpna_type) {
      case StcsFpnaTypeEnum.SFT_CTX_LINE_GROWTH:
      case StcsFpnaTypeEnum.SFT_CTX_LINE_IPO:
        return false
      default:
        return true
    }
  }

  isEnabled(node: IRowNode, gridApi: GridApi): boolean {
    return this.isActionAllowed(node) && this.isStatementSpecificEnabled(node, gridApi)
  }

  onClick(event: MouseEvent, node: IRowNode, gridApi: GridApi): void {
    this.executeStatementSpecificOnClick(event, node, gridApi)
  }

  getColDef(gridApi: GridApi): ColDef {
    return {
      headerName: this.headerName,
      field: this.id,
      cellRenderer: this.id,
      cellRendererParams: {
        gridApi: gridApi,
      },
      headerClass: 'text-center',
      suppressMovable: true,
      cellStyle: { textAlign: 'center' },
      menuTabs: [],
      resizable: true,
      width: this.minWidth,
      minWidth: this.minWidth,
      flex: this.flex,
      suppressHeaderContextMenu: true,
      headerComponent: OnboardingCustomHeader,
      headerComponentParams: { headerTooltip: this.headerTooltip },
    }
  }

  public abstract isStatementSpecificEnabled(node: IRowNode, gridApi: GridApi): boolean

  public abstract executeStatementSpecificOnClick(event: MouseEvent, node: IRowNode, gridApi: GridApi): void

  public abstract getElement(node: IRowNode, gridApi: GridApi): HTMLElement
}

export class DummyActionButton extends AcmGridActionButtonStrategy {
  override headerName = ''
  override minWidth = 150
  override id = Date.now().toString()

  public override getElement(): HTMLElement {
    return document.createElement('')
  }

  public override isStatementSpecificEnabled(): boolean {
    return false
  }

  public override executeStatementSpecificOnClick(_event: MouseEvent, node: IRowNode, _gridApi: GridApi): void {
    consola.debug(this.id, ' clicked on ', node)
  }
}

export class IntercompanyActionButton extends AcmGridActionButtonStrategy {
  override id = 'intercompany'
  override headerName = this.$tolgee.t('mapping_headerName_intercompany')
  override headerTooltip = this.$tolgee.t('mapping_tooltip_intercompany')
  override minWidth = 150

  public override getElement(node: IRowNode): HTMLElement {
    const checkbox = document.createElement('input')
    checkbox.type = 'checkbox'
    checkbox.className = 'ag-checkbox'
    checkbox.checked = this.acmStore.getMappingItem(node)?.is_inter_comp ?? false
    checkbox.addEventListener('change', (event) => {
      const target = event.target as HTMLInputElement
      const checked = target.checked
      const mappingItem = this.acmStore.getMappingItem(node)
      if (mappingItem) {
        mappingItem.is_inter_comp = checked
        this.acmStore.updateAccMappingData()
      }
    })

    return checkbox
  }

  public override isStatementSpecificEnabled(node: IRowNode, _gridApi: GridApi): boolean {
    return node?.data?.fpna_type === StcsFpnaTypeEnum.SFT_ACC_ITEM
  }

  public override executeStatementSpecificOnClick(_event: MouseEvent, node: IRowNode, _gridApi: GridApi): void {
    consola.debug(this.id, ' clicked on ', node)
  }
}

export class NetDebtActionButton extends AcmGridActionButtonStrategy {
  override id = 'net_debt'
  override headerName = this.$tolgee.t('mapping_headerName_net_debt')
  override headerTooltip = this.$tolgee.t('mapping_tooltip_net_debt')
  override minWidth = 150

  public override getElement(node: IRowNode): HTMLElement {
    const checkbox = document.createElement('input')
    checkbox.type = 'checkbox'
    checkbox.className = 'ag-checkbox'
    checkbox.checked = this.acmStore.getMappingItem(node)?.net_debt ?? false
    checkbox.addEventListener('change', (event) => {
      const target = event.target as HTMLInputElement
      const checked = target.checked
      const mappingItem = this.acmStore.getMappingItem(node)

      if (mappingItem) {
        mappingItem.net_debt = checked
        this.acmStore.updateAccMappingData()
      }
    })

    return checkbox
  }

  public override isStatementSpecificEnabled(node: IRowNode, _gridApi: GridApi): boolean {
    return node?.data?.fpna_type === StcsFpnaTypeEnum.SFT_ACC_ITEM
  }

  public override executeStatementSpecificOnClick(_event: MouseEvent, node: IRowNode, _gridApi: GridApi): void {
    consola.debug(this.id, ' clicked on ', node)
  }
}

export class CreditDebitActionButton extends AcmGridActionButtonStrategy {
  override id = 'credit_debit'
  override headerName = this.$tolgee.t('mapping_headerName_credit_debit')
  override headerTooltip = this.$tolgee.t('mapping_tooltip_credit_debit')
  override minWidth = 150

  public override getElement(): HTMLElement {
    const container = document.createElement('div')
    return container as HTMLElement
  }

  public override isStatementSpecificEnabled(node: IRowNode, _gridApi: GridApi): boolean {
    return node?.data?.fpna_type === StcsFpnaTypeEnum.SFT_ACC_ITEM
  }

  public override executeStatementSpecificOnClick(_event: MouseEvent, node: IRowNode, _gridApi: GridApi): void {
    consola.debug(this.id, ' clicked on ', node)
  }
}

export class TagsActionButton extends AcmGridActionButtonStrategy {
  override id = 'tags'
  override headerName = this.$tolgee.t('mapping_headerName_tags')
  override headerTooltip = this.$tolgee.t('mapping_tooltip_tags')
  override minWidth = 300
  override flex = 1

  override getColDef(gridApi: GridApi): ColDef {
    return {
      ...super.getColDef(gridApi),
      width: 300,
    }
  }

  public override getElement(): HTMLElement {
    const container = document.createElement('div')
    return container as HTMLElement
  }

  public override isStatementSpecificEnabled(node: IRowNode, _gridApi: GridApi): boolean {
    return node?.data?.fpna_type === StcsFpnaTypeEnum.SFT_ACC_ITEM
  }

  public override executeStatementSpecificOnClick(_event: MouseEvent, node: IRowNode, _gridApi: GridApi): void {
    consola.debug(this.id, ' clicked on ', node)
  }
}
